import {Astro, Gender, Orientation, Orientation2LabelMapping, Pronouns, Pronouns2LabelMapping, Search, SubscriptionType} from '@enums';
import {logger} from '@services';
import {DocumentData, FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions} from 'firebase/firestore';
import {isEqual} from 'underscore';
import {BaseFirebaseModel} from '../bases/baseFirebaseModel';
import {Badge} from '../Common/Badge';
import {Kink} from '../Common/Kink';
import {OnboardingProgress, OnboardingStep} from '../Engagement/OnboardingProgress';
import {TestResult} from '../Engagement/TestResult';
import {Lang} from '../Settings/Lang';
import {Match} from './Match';

export class Profile extends BaseFirebaseModel {
	public static defaultImg = '/assets/profil_image.webp';
	public static defaultUsername: string = 'SlaveousUser';
	public static override className = 'Profile';
	private static readonly astroRanges = [
		{start: 321, end: 420, sign: Astro.Aries},
		{start: 421, end: 520, sign: Astro.Taurus},
		{start: 521, end: 621, sign: Astro.Gemini},
		{start: 622, end: 722, sign: Astro.Cancer},
		{start: 723, end: 822, sign: Astro.Leo},
		{start: 823, end: 922, sign: Astro.Virgo},
		{start: 923, end: 1022, sign: Astro.Libra},
		{start: 1023, end: 1122, sign: Astro.Scorpio},
		{start: 1123, end: 1221, sign: Astro.Sagittarius},
		{start: 1222, end: 1231, sign: Astro.Capricorn},
		{start: 101, end: 120, sign: Astro.Capricorn},
		{start: 121, end: 219, sign: Astro.Aquarius},
		{start: 220, end: 320, sign: Astro.Pisces}
	];
	private static readonly relationAliasMap: Record<string, Search> = {
		'ami': Search.Amitie,
		'vi': Search.Virtuel,
		'relle': Search.Reel,
		'bdsm': Search.Bdsm,
		'am': Search.Amour,
		'seance': Search.Seances
	};
	private static readonly orientationAliasMap: Record<string, Orientation> = {
		'hetero': Orientation['Hétérosexuel(le)'],
		'pan': Orientation['Pansexue(le)'],
		'bi': Orientation['Bisexuel(le)'],
		'demi': Orientation['Demisexuel(le)']
	};
	isBlocked!: boolean;
	isFavorite!: boolean;
	friends: string[] = [];
	transPeople!: boolean;
	activeSound = true;
	astroSign: Astro = 0;
	ref: unknown;
	public email!: string;
	username!: string;
	gender!: number;
	activite: unknown;
	orientation!: (string | number)[];
	pays!: string;
	region!: string;
	isEternal!: boolean;
	subscriptionType: SubscriptionType = SubscriptionType.Classique;
	subscriptionExpiresAt?: number;
	galleryUrls: string[] = [];
	onboarding?: OnboardingProgress;
	role!: number;
	description!: string;
	status!: number;
	shortDesc!: string;
	date!: string;
	relation!: (string | number)[];
	practices_objects: Map<string, boolean> = new Map<string, boolean>();
	practices!: string[];
	pratiques!: string[];
	color = '#c4bcbc';
	test!: TestResult[];
	newPassword = '';
	passwordConfirm = '';
	mailNotify!: boolean;
	mailLimite5!: boolean;
	favorits: string[] = [];
	blocked: string[] = [];
	scope!: string;
	pronoun: Pronouns[] = [];
	badges: string[] = [];
	lang!: Lang;
	emailVerified!: boolean;
	lastLoginAt?: number;
	public formatedBadges: Badge[] = [];
	hasImage!: boolean;
	imgUrl: string = Profile.defaultImg;
	dbFilled!: boolean;
	private initValue?: Profile;

	constructor(obj?: Record<string, unknown>) {
		super();
		if (!obj) {
			return;
		}
		Object.assign(this, obj);
		this.lang = Profile.ensureLang(this.lang);
		this.initProfile();
		this.setInitValue();
	}

	set _imgUrl(url: string) {
		if (url !== Profile.defaultImg) {
			this.imgUrl = url;
		}
	}

	get age() {
		return Math.floor(
			Math.abs(Date.now() - new Date(this?.date).getTime()) /
			(1000 * 3600 * 24) /
			365.25
		);
	}

	set age(value) {
	}

	private initProfile() {
		this.convertPractices();
		this.normalizeOrientationInputs();
		this.normalizeRelationInputs();
		this.normalizePronounInputs();
		if (!Array.isArray(this.galleryUrls)) {
			this.galleryUrls = [];
		}
		if (this.subscriptionType === undefined || this.subscriptionType === null) {
			this.subscriptionType = SubscriptionType.Classique;
		}
		if (this.date) {
			this.Compute();
		}
	}

	setInitValue() {
		this.initValue = Object.assign({}, this);
		this.initValue.favorits = this.favorits ? [...this.favorits] : [];
		this.initValue.blocked = this.blocked ? [...this.blocked] : [];
		this.initValue.pronoun = this.pronoun ? [...this.pronoun] : [];
		this.initValue.practices_objects = new Map([...this.practices_objects]);
		this.initValue.test = this.test ? [...this.test] : [];
		this.initValue.orientation = this.orientation ? [...this.orientation] : [];
		this.initValue.friends = this.friends ? [...this.friends] : [];
		this.initValue.badges = this.badges ? [...this.badges] : [];
		this.initValue.lang = Profile.ensureLang(this.lang);
		this.initValue.galleryUrls = this.galleryUrls ? [...this.galleryUrls] : [];
		this.initValue.subscriptionType = this.subscriptionType;
		this.initValue.subscriptionExpiresAt = this.subscriptionExpiresAt;
		this.initValue.onboarding = this.onboarding ? {
			...this.onboarding,
			completedSteps: [...this.onboarding.completedSteps]
		} : undefined;
	}

	private static defaultOnboarding(): OnboardingProgress {
		return {
			currentStep: OnboardingStep.Profile,
			completedSteps: [],
			lastUpdatedAt: Date.now()
		};
	}

	public getOnboardingProgress(): OnboardingProgress {
		if (!this.onboarding) {
			return Profile.defaultOnboarding();
		}
		return {
			...this.onboarding,
			completedSteps: [...(this.onboarding.completedSteps ?? [])]
		};
	}

	public Compute() {
		const astroSign = Profile.getAstroSign(this.date);
		if (astroSign !== undefined) {
			this.astroSign = astroSign;
		}
	}

	convertPractices() {
		if (this.practices_objects instanceof Map && this.practices_objects.size > 0) {
			this.practices_objects = new Map(this.practices_objects);
			return;
		}
		const source = this.practices_objects && !(this.practices_objects instanceof Map)
			? this.practices_objects
			: (this.practices ?? this.pratiques);
		if (!source) {
			return;
		}
		this.practices_objects = Profile.mapPracticeSourceToMap(source);
	}

	private normalizeOrientationInputs() {
		if (!Array.isArray(this.orientation)) {
			return;
		}
		const normalized = this.orientation
			.map(value => Profile.convertOrientationValue(value))
			.filter((value): value is Orientation => value !== undefined);
		if (normalized.length > 0) {
			this.orientation = normalized;
		}
	}

	private normalizeRelationInputs() {
		if (!Array.isArray(this.relation)) {
			return;
		}
		const normalized = this.relation
			.map(value => Profile.convertRelationValue(value))
			.filter((value): value is Search => value !== undefined);
		if (normalized.length > 0) {
			this.relation = normalized;
		}
	}

	private normalizePronounInputs() {
		const raw = this.pronoun as unknown;
		if (raw === null || raw === undefined) {
			this.pronoun = [];
			return;
		}
		if (Array.isArray(raw)) {
			this.pronoun = raw
				.map(value => Profile.convertPronounValue(value as string | number))
				.filter((value): value is Pronouns => value !== undefined);
			return;
		}
		if (typeof raw === 'string') {
			const parts = raw.split(/[;,]/).map(value => value.trim()).filter(Boolean);
			if (parts.length > 0) {
				this.pronoun = parts
					.map(value => Profile.convertPronounValue(value))
					.filter((value): value is Pronouns => value !== undefined);
				return;
			}
		}
		if (typeof raw === 'object') {
			const entries = Object.entries(raw as Record<string, unknown>);
			this.pronoun = entries
				.filter(([, value]) => value === true)
				.map(([key]) => Profile.convertPronounValue(key))
				.filter((value): value is Pronouns => value !== undefined);
			return;
		}
		const normalized = Profile.convertPronounValue(raw as string | number);
		this.pronoun = normalized !== undefined ? [normalized] : [];
	}

	private static mapPracticeSourceToMap(source: unknown) {
		const practicesMap = new Map<string, boolean>();
		if (Array.isArray(source)) {
			source.forEach(practice => {
				if (typeof practice === 'string') {
					practicesMap.set(practice, true);
				}
			});
			return practicesMap;
		}

		if (typeof source === 'object' && source !== null) {
			const sourceRecord = source as Record<string, unknown>;
			Object.entries(sourceRecord).forEach(([key, value]) => {
				if (value === true) {
					practicesMap.set(key, true);
				}
			});
		}

		return practicesMap;
	}

	private static convertOrientationValue(value: string | number): Orientation | undefined {
		if (typeof value === 'number' && !Number.isNaN(value)) {
			return value as Orientation;
		}
		const asNumber = Number(value);
		if (!Number.isNaN(asNumber)) {
			return asNumber as Orientation;
		}
		const enumKey = value as keyof typeof Orientation;
		if (enumKey in Orientation) {
			return Orientation[enumKey];
		}
		const alias = Profile.orientationAliasMap[String(value).toLowerCase()];
		if (alias !== undefined) {
			return alias;
		}
		const labelEntry = Object.entries(Orientation2LabelMapping)
			.find(([, label]) => label.toLowerCase() === String(value).toLowerCase());
		if (labelEntry) {
			return Number(labelEntry[0]) as Orientation;
		}
		return undefined;
	}

	private static convertRelationValue(value: string | number): Search | undefined {
		if (typeof value === 'number' && !Number.isNaN(value)) {
			return value as Search;
		}
		const enumKey = value as keyof typeof Search;
		if (enumKey in Search) {
			return Search[enumKey];
		}
		const alias = Profile.relationAliasMap[String(value).toLowerCase()];
		if (alias !== undefined) {
			return alias;
		}
		return undefined;
	}

	private static convertPronounValue(value: string | number): Pronouns | undefined {
		if (typeof value === 'number' && !Number.isNaN(value)) {
			return value as Pronouns;
		}
		const asNumber = Number(value);
		if (!Number.isNaN(asNumber)) {
			return asNumber as Pronouns;
		}
		const enumKey = value as keyof typeof Pronouns;
		if (enumKey in Pronouns) {
			return Pronouns[enumKey];
		}
		const labelEntry = Object.entries(Pronouns2LabelMapping)
			.find(([, label]) => label.toLowerCase() === String(value).toLowerCase());
		if (labelEntry) {
			return Number(labelEntry[0]) as Pronouns;
		}
		return undefined;
	}

	private static ensureLang(value?: Lang | Record<string, unknown>): Lang {
		if (value instanceof Lang) {
			return new Lang(value.shortCode, value.country, value.language);
		}
		if (typeof value === 'object' && value !== null) {
			const shortCode = String((value as unknown as Lang).shortCode ?? 'en');
			const country = String((value as unknown as Lang).country ?? 'England');
			const language = String((value as unknown as Lang).language ?? 'english');
			return new Lang(shortCode, country, language);
		}
		return new Lang('en', 'England', 'english');
	}

	private static getAstroSign(date?: string): Astro | undefined {
		const mmdd = Profile.extractDateCode(date);
		if (mmdd === undefined) {
			return undefined;
		}
		for (const range of Profile.astroRanges) {
			if (mmdd >= range.start && mmdd <= range.end) {
				return range.sign;
			}
		}
		return undefined;
	}

	private static extractDateCode(date?: string): number | undefined {
		if (!date) {
			return undefined;
		}
		const parsed = new Date(date);
		if (Number.isNaN(parsed.getTime())) {
			return undefined;
		}
		const month = parsed.getMonth() + 1;
		const day = parsed.getDate();
		return month * 100 + day;
	}

	/** Min length for Firebase-compatible password. */
	static readonly PASSWORD_MIN_LENGTH = 8;

	/** Max length for password (align with Firebase/Identity Platform). */
	static readonly PASSWORD_MAX_LENGTH = 128;

	/**
	 * Validates password against Firebase-style requirements:
	 * at least one uppercase, one lowercase, one digit, one non-alphanumeric character.
	 */
	checkPasswordPaterne(): boolean {
		const pwd = this.newPassword ?? '';
		if (pwd.length < Profile.PASSWORD_MIN_LENGTH || pwd.length > Profile.PASSWORD_MAX_LENGTH) {
			return false;
		}
		return (
			/[A-Z]/.test(pwd) &&
			/[a-z]/.test(pwd) &&
			/\d/.test(pwd) &&
			/[^A-Za-z0-9]/.test(pwd)
		);
	}

	checkEmailPaterne() {
		return new RegExp(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-z]{2,}))$/i).test(this.email);
	}

	checkUsername() {
		return new RegExp(/^[a-zA-Z0-9àèìòùÀÈÌÒÙáéíóúýÁÉÍÓÚÝâêîôûÂÊÎÔÛãñõÃÑÕäëïöüÿÄËÏÖÜŸçÇßØøÅåÆæœ_]{1,16}$/i).test(this.username);
	}

	realtimeUpdate() {
		const updates = new Map();
		const basePath = '/profile/' + this.uuid + '/';
		const nullobj = {};
		if (!this.initValue) {
			return nullobj;
		} else {

			updates.set(basePath + 'imgUrl', null);
			updates.set(basePath + 'hasImage', null);

			if (this.initValue.activeSound !== this.activeSound && this.activeSound !== undefined) {
				updates.set(basePath + 'activeSound', this.activeSound);
			}
			if (this.initValue.color !== this.color && this.color !== undefined) {
				updates.set(basePath + 'color', this.color);
			}
			if (this.initValue.description !== this.description && this.description !== undefined) {
				updates.set(basePath + 'description', this.description);
			}
			if (this.initValue.shortDesc !== this.shortDesc && this.shortDesc !== undefined) {
				updates.set(basePath + 'shortDesc', this.shortDesc);
			}
			if (this.initValue.mailLimite5 !== this.mailLimite5 && this.mailLimite5 !== undefined) {
				updates.set(basePath + 'mailLimite5', this.mailLimite5);
			}
			if (this.initValue.mailNotify !== this.mailNotify && this.mailNotify !== undefined) {
				updates.set(basePath + 'mailNotify', this.mailNotify);
			}
			if (this.badges && !isEqual(this.initValue.badges?.sort(), this.badges.sort()) && this.initValue.badges?.length - this.badges.length !== 0) {
				updates.set(basePath + 'badges', this.badges);
			}

			// Check if practices have changed
			const practicesChanged = !isEqual(
				Array.from(this.initValue.practices_objects.entries()).sort(),
				Array.from(this.practices_objects.entries()).sort()
			) || this.initValue.practices_objects.size !== this.practices_objects.size;

			if (practicesChanged) {
				// Update or add new practices
				this.practices_objects.forEach((value, key) => {
					updates.set(basePath + 'practices/' + key, value ? true : null);
				});
				// Remove practices that were in initValue but are no longer in practices_objects
				this.initValue.practices_objects.forEach((value, key) => {
					if (!this.practices_objects.has(key)) {
						updates.set(basePath + 'practices/' + key, null);
					}
				});
			}
			if (this.friends && !isEqual(this.initValue.friends.sort(), this.friends.sort()) && this.initValue.friends.length - this.friends.length !== 0) {
				updates.set(basePath + 'friends', this.friends);
			}
			if (this.favorits && !isEqual(this.initValue.favorits.sort(), this.favorits.sort()) && this.initValue.favorits.length - this.favorits.length !== 0) {
				updates.set(basePath + 'favorits', this.favorits);
			}
			if (this.blocked && !isEqual(this.initValue.blocked.sort(), this.blocked.sort()) && this.initValue.blocked.length - this.blocked.length !== 0) {
				updates.set(basePath + 'blocked', this.blocked);
			}
			if (this.lang && !isEqual(this.initValue.lang?.shortCode, this.lang.shortCode)) {
				updates.set(basePath + 'lang', this.lang.toFirebase());
			}
			if (!isEqual(this.initValue?.createdAt, this.createdAt)) {
				updates.set(basePath + 'createdAt', this.createdAt);
			}
			if (!isEqual(this.initValue?.updatedAt, this.updatedAt)) {
				updates.set(basePath + 'updatedAt', this.updatedAt);
			}
			if (this.initValue.subscriptionType !== this.subscriptionType && this.subscriptionType !== undefined) {
				updates.set(basePath + 'subscriptionType', this.subscriptionType);
			}
			if (this.initValue.subscriptionExpiresAt !== this.subscriptionExpiresAt) {
				updates.set(basePath + 'subscriptionExpiresAt', this.subscriptionExpiresAt ?? null);
			}
			if (this.galleryUrls && !isEqual(this.initValue.galleryUrls?.slice().sort(), this.galleryUrls.slice().sort()) || (this.initValue.galleryUrls?.length ?? 0) !== (this.galleryUrls?.length ?? 0)) {
				updates.set(basePath + 'galleryUrls', this.galleryUrls ?? []);
			}

		}
		// @ts-expect-error - Type assertion needed for Map to object conversion
		return updates !== nullobj ? <object> [...updates].reduce((o, [key, value]) => (o[key] = value, o), {}) : nullobj;
	}

	firestoreUpdate() {
		const update = new Map();
		try {
			if (this.initValue) {
				if (this.initValue.hasImage !== this.hasImage) {
					update.set('hasImage', this.hasImage);
				}
				if (this.hasImage) {
					if (this.imgUrl !== this.initValue.imgUrl) {
						update.set('imgUrl', this.imgUrl);
					}
				}
				if (this.date !== undefined && this.initValue.date !== this.date) {
					update.set('date', this.date);
				}
				if (this.initValue.gender !== this.gender && this.gender !== undefined) {
					update.set('gender', this.gender);
				}
				if (this.relation && (this.initValue.relation?.sort() !== this.relation.sort() || this.initValue.relation?.length !== this.relation.length)) {
					update.set('relation', this.relation);
				}
				if (this.orientation && (this.initValue.orientation?.sort() !== this.orientation.sort() || this.initValue.orientation?.length !== this.orientation.length)) {
					update.set('orientation', this.orientation);
				}
				if (this.initValue.pays !== this.pays && this.pays !== undefined) {
					update.set('pays', this.pays);
				}
				if (this.initValue.region !== this.region && this.region !== undefined) {
					update.set('region', this.region);
				}
				if (this.initValue.transPeople !== this.transPeople && this.transPeople !== undefined) {
					update.set('transPeople', this.transPeople);
				}
				if (this.initValue.username !== this.username && this.username !== undefined) {
					update.set('username', this.username);
				}
				if (this.initValue.role !== this.role && this.role !== undefined) {
					update.set('role', this.role);
				}
				if (this.initValue.shortDesc !== this.shortDesc && this.shortDesc !== undefined) {
					update.set('shortDesc', this.shortDesc);
				}
				if (this.test && !isEqual(this.initValue.test?.sort(), this.test.sort()) && this.test.length !== 0) {
					update.set('test', this.test);
				}
				if (this.favorits && !isEqual(this.initValue.favorits?.slice().sort(), this.favorits.slice().sort())) {
					update.set('favorits', this.favorits);
				}
				if (this.blocked && !isEqual(this.initValue.blocked?.slice().sort(), this.blocked.slice().sort())) {
					update.set('blocked', this.blocked);
				}
				if (this.friends && !isEqual(this.initValue.friends?.slice().sort(), this.friends.slice().sort())) {
					update.set('friends', this.friends);
				}
				if (!isEqual(this.initValue?.createdAt, this.createdAt)) {
					update.set('createdAt', this.createdAt);
				}
				if (!isEqual(this.initValue.status, this.status)) {
					update.set('status', this.status);
				}
				if (this.pronoun && (!isEqual(this.initValue.pronoun?.sort(), this.pronoun.sort() || this.initValue.pronoun?.length - this.pronoun.length !== 0))) {
					update.set('pronoun', this.pronoun);
				}
				if (this.emailVerified !== undefined || this.initValue.emailVerified !== this.emailVerified) {
					update.set('emailVerified', this.emailVerified);
				}
				if (this.initValue.subscriptionType !== this.subscriptionType && this.subscriptionType !== undefined) {
					update.set('subscriptionType', this.subscriptionType);
				}
				if (this.initValue.subscriptionExpiresAt !== this.subscriptionExpiresAt) {
					update.set('subscriptionExpiresAt', this.subscriptionExpiresAt ?? null);
				}
				if (!isEqual(this.initValue.galleryUrls?.length, this.galleryUrls?.length) || !isEqual(this.initValue.galleryUrls?.slice().sort(), this.galleryUrls?.slice().sort())) {
					update.set('galleryUrls', this.galleryUrls ?? []);
				}
				if (!isEqual(this.initValue.onboarding, this.onboarding) && this.onboarding !== undefined) {
					update.set('onboarding', this.onboarding);
				}
				if (update.size !== 0) {
					update.set('updatedAt', Date.now());
				}
				if (this.initValue.gender !== this.gender && this.gender !== undefined) {
					update.set('gender', this.gender);
				}
				if (this.initValue.lang?.shortCode !== this.lang?.shortCode && this.lang !== undefined) {
					update.set('lang', this.lang.toFirebase());
				}
			}
		} catch (e) {
			logger.error(e);
			throw e;
		}

		try {
			// @ts-expect-error - Type assertion needed for Map to object conversion
			return <object> [...update].reduce((o, [key, value]) => (o[key] = value, o), {});
		} catch (e) {
			logger.error(e);
			throw e;
		}
	}

	ComputeBadges(blist: Badge[]) {
		this.formatedBadges = this.badges?.map(buid => {
			const badge = blist.find(b => b.uuid === buid);
			if (badge) {
				badge.used = true;
			}
			return <Badge> badge;
		}).filter(b => b && b.used);
	}

	merge(tempProfile: Profile): void {
		for (const [key, value] of Object.entries(tempProfile)) {
			if (value !== undefined) {
				(this as Record<string, unknown>)[key] = value;
			}
		}
		this.initValue!.initValue = undefined;
		if (this.imgUrl === Profile.defaultImg) {
			this.imgUrl = tempProfile.imgUrl;
		}
		this.initProfile();
	}

	public toJson() {
		return {
			uuid: this.uuid,
			username: this.username,
			date: this.date,
			shortDesc: this.shortDesc,
			description: this.description,
			relation: this.relation,
			role: this.role,
			gender: this.gender,
			pays: this.pays,
			region: this.region,
			orientation: this.orientation,
			badges: this.badges,
			imgUrl: this.imgUrl,
			activeSound: this.activeSound,
			lang: this.lang,
			createdAt: this.createdAt,
			updatedAt: this.updatedAt,
			dbFilled: this.dbFilled
		};
	}

	toAdminJson(): Record<string, unknown> {
		return {
			...this.toJson(),
			email: this.email,
			emailVerified: this.emailVerified,
			admin: this.scope === 'Admin'
		};
	}

	fullRealtimeUpdate() {
		const updates = new Map();
		const basePath = '/profile/' + this.uuid + '/';
		const nullobj = {};

		updates.set(basePath + 'imgUrl', null);
		updates.set(basePath + 'hasImage', null);
		updates.set(basePath + 'activeSound', this.activeSound);
		updates.set(basePath + 'color', this.color);
		updates.set(basePath + 'description', this.description);
		updates.set(basePath + 'mailLimite5', this.mailLimite5);
		updates.set(basePath + 'mailNotify', this.mailNotify);
		updates.set(basePath + 'badges', this.badges);
		this.practices_objects.forEach((value, key) => {
			updates.set(basePath + 'practices/' + key, value ? true : null);
		});
		updates.set(basePath + 'friends', this.friends);
		updates.set(basePath + 'favorits', this.favorits);
		updates.set(basePath + 'blocked', this.blocked);
		updates.set(basePath + 'lang', this.lang);
		updates.set(basePath + 'createdAt', this.createdAt);
		updates.set(basePath + 'updatedAt', Date.now());
		updates.set(basePath + 'subscriptionType', this.subscriptionType);
		updates.set(basePath + 'subscriptionExpiresAt', this.subscriptionExpiresAt ?? null);
		updates.set(basePath + 'galleryUrls', this.galleryUrls ?? []);

		updates.set(basePath + 'date', null);
		updates.set(basePath + 'gender', null);
		updates.set(basePath + 'relation', null);
		updates.set(basePath + 'orientation', null);
		updates.set(basePath + 'pays', null);
		updates.set(basePath + 'region', null);
		updates.set(basePath + 'transPeople', null);
		updates.set(basePath + 'username', null);
		updates.set(basePath + 'role', null);
		updates.set(basePath + 'shortDesc', null);
		updates.set(basePath + 'status', null);
		updates.set(basePath + 'pronoun', null);
		updates.set(basePath + 'transPeople', null);
		updates.set(basePath + 'gender', null);
		updates.set(basePath + 'scope', null);

		// @ts-expect-error - Type assertion needed for Map to object conversion
		return updates !== nullobj ? <object> [...updates].reduce((o, [key, value]) => (o[key] = value ?? null, o), {}) : nullobj;
	}

	fullFirestoreUpdate() {
		const update = new Map();
		update.set('hasImage', this.hasImage);
		if (this.imgUrl !== Profile.defaultImg) {
			update.set('imgUrl', this.imgUrl);
		} else {
			update.set('imgUrl', null);
		}
		update.set('date', this.date);
		update.set('gender', this.gender);
		update.set('relation', this.relation);
		update.set('orientation', this.orientation);
		update.set('pays', this.pays);
		update.set('region', this.region);
		update.set('transPeople', this.transPeople);
		update.set('username', this.username);
		update.set('role', this.role);
		update.set('shortDesc', this.shortDesc);
		update.set('createdAt', this.createdAt);
		update.set('status', this.status);
		update.set('pronoun', this.pronoun);
		update.set('transPeople', this.transPeople);
		update.set('updatedAt', Date.now());
		update.set('gender', this.gender);
		update.set('scope', this.scope);
		update.set('subscriptionType', this.subscriptionType);
		update.set('subscriptionExpiresAt', this.subscriptionExpiresAt ?? null);
		update.set('galleryUrls', this.galleryUrls ?? []);

		update.set('activeSound', null);
		update.set('color', null);
		update.set('description', null);
		update.set('mailLimite5', null);
		update.set('mailNotify', null);
		update.set('badges', null);
		update.set('practices', null);
		update.set('friends', null);
		update.set('favorits', null);
		update.set('test', null);
		update.set('blocked', null);
		update.set('lang', null);

		try {
			// @ts-expect-error - Type assertion needed for Map to object conversion
			return <object> [...update].reduce((o, [key, value]) => (o[key] = value ?? null, o), {});
		} catch (e) {
			logger.error(e);
			throw e;
		}
	}

	checkGender(): boolean {
		return this.gender !== undefined && this.gender !== null;
	}

	/**
	 * Calculate compatibility score with another profile based on test results and kinks
	 * @param otherProfile The profile to compare with
	 * @returns A compatibility score from 0 to 100, or null if insufficient data
	 */
	calculateCompatibilityScore(otherProfile: Profile): number | null {
		if (!this.test || !otherProfile.test || this.test.length === 0 || otherProfile.test.length === 0) {
			return null;
		}

		if (this.hasOrientationMismatch(otherProfile)) {
			return 0;
		}

		// Convert test results to Kink objects for comparison
		const currentKinks = this.test.map(t => new Kink(t.role, t.average, t.date));
		const otherKinks = otherProfile.test.map(t => new Kink(t.role, t.average, t.date));

		const avgCompatibility = (
			this.computeCompatibility(currentKinks, otherKinks) +
			this.computeCompatibility(otherKinks, currentKinks)
		) / 2;

		// Calculate kinks/practices overlap
		let practicesScore = 0;
		if (this.practices_objects && otherProfile.practices_objects) {
			const currentPractices = Array.from(this.practices_objects.keys()).filter(k => this.practices_objects.get(k));
			const otherPractices = Array.from(otherProfile.practices_objects.keys()).filter(k => otherProfile.practices_objects.get(k));

			if (currentPractices.length > 0 || otherPractices.length > 0) {
				const commonPractices = currentPractices.filter(p => otherPractices.includes(p));
				const totalPractices = new Set([...currentPractices, ...otherPractices]).size;
				practicesScore = totalPractices > 0 ? (commonPractices.length / totalPractices) * 100 : 0;
			}
		}

		// Weighted average: 80% test compatibility, 20% practices
		const testScore = avgCompatibility * 0.8;
		const finalScore = testScore + (practicesScore * 0.2);

		return Math.round(Math.min(100, Math.max(0, finalScore)));
	}

	private computeCompatibility(sourceKinks: Kink[], targetKinks: Kink[]): number {
		let compatibilitySum = 0;
		let compatibilityCount = 0;

		sourceKinks.forEach(currentKink => {
			const targetLabel = Match.getOpposite(currentKink.label);
			if (!targetLabel) {
				return;
			}
			const matchingKink = targetKinks.find(k => k.label === targetLabel);
			const otherValue = matchingKink ? matchingKink.value : 0;
			const compatibility = 100 - Math.abs(currentKink.value - otherValue);
			compatibilitySum += compatibility;
			compatibilityCount++;
		});

		return compatibilityCount > 0 ? compatibilitySum / compatibilityCount : 0;
	}

	private hasOrientationMismatch(otherProfile: Profile): boolean {
		if (!this.orientation || !otherProfile.orientation || this.gender === undefined || otherProfile.gender === undefined) {
			return false;
		}
		const currentOrientations = this.normalizeOrientations(this.orientation);
		const otherOrientations = this.normalizeOrientations(otherProfile.orientation);
		if (currentOrientations.size === 0 || otherOrientations.size === 0) {
			return false;
		}
		const currentGender = this.gender as Gender;
		const otherGender = otherProfile.gender as Gender;
		return !this.isAttractedTo(currentOrientations, otherGender, currentGender)
			|| !this.isAttractedTo(otherOrientations, currentGender, otherGender);
	}

	private normalizeOrientations(orientations: Array<string | number>): Set<Orientation> {
		const normalized = new Set<Orientation>();
		orientations.forEach((value) => {
			if (typeof value === 'number') {
				normalized.add(value as Orientation);
				return;
			}
			const numeric = Number(value);
			if (!Number.isNaN(numeric)) {
				normalized.add(numeric as Orientation);
				return;
			}
			if (value in Orientation) {
				// @ts-expect-error - dynamic enum access
				normalized.add(Orientation[value]);
				return;
			}
			const labelEntry = Object.entries(Orientation2LabelMapping)
				.find(([, label]) => label.toLowerCase() === value.toLowerCase());
			if (labelEntry) {
				normalized.add(Number(labelEntry[0]) as Orientation);
				return;
			}
			switch (value) {
				case 'hetero':
					normalized.add(Orientation['Hétérosexuel(le)']);
					break;
				case 'pan':
					normalized.add(Orientation['Pansexue(le)']);
					break;
				case 'bi':
					normalized.add(Orientation['Bisexuel(le)']);
					break;
				case 'demi':
					normalized.add(Orientation['Demisexuel(le)']);
					break;
				default:
					break;
			}
		});
		return normalized;
	}

	private isAttractedTo(orientations: Set<Orientation>, targetGender: Gender, selfGender: Gender): boolean {
		if (orientations.has(Orientation['Asexuel(le)'])) {
			return true;
		}
		if (
			orientations.has(Orientation['Pansexue(le)']) ||
			orientations.has(Orientation.Queer) ||
			orientations.has(Orientation['Autre orientation']) ||
			orientations.has(Orientation['Demisexuel(le)'])
		) {
			return true;
		}
		if (orientations.has(Orientation['Bisexuel(le)'])) {
			return true;
		}

		const isMale = selfGender === Gender.Homme;
		const isFemale = selfGender === Gender.Femme;
		const targetIsMale = targetGender === Gender.Homme;
		const targetIsFemale = targetGender === Gender.Femme;

		if (orientations.has(Orientation['Hétérosexuel(le)'])) {
			return (isMale && targetIsFemale) || (isFemale && targetIsMale);
		}
		if (orientations.has(Orientation.Gay)) {
			return isMale && targetIsMale;
		}
		if (orientations.has(Orientation.Lesbienne)) {
			return isFemale && targetIsFemale;
		}
		return true;
	}

	setLang(lang: string): void {
		this.lang = new Lang(lang, this.pays ?? '', Lang.shortCodeToLangue(lang));
	}

}

export const profileConverter: FirestoreDataConverter<Profile, DocumentData> = {
	toFirestore: (profile: Profile): DocumentData => {
		return profile.firestoreUpdate() as DocumentData;
	},
	fromFirestore: (snapshot: QueryDocumentSnapshot<DocumentData>, options?: SnapshotOptions): Profile => {
		const data = snapshot.data(options);
		return new Profile({uuid: snapshot.id, ...data});
	}
};
